'use client'

import { useEffect, useMemo, useRef } from 'react'
import { useActiveHeading } from './useActiveHeading'

export interface TOCHeading {
  id: string
  text: string
  level: number
}

interface Props {
  headings: TOCHeading[]
}

export function TableOfContents({ headings }: Props) {
  const { activeId, pin } = useActiveHeading(headings)

  // h2 id -> child h3 ids for expand/collapse, plus which h3s got a parent. An h3 before the first
  // h2 has none, so it can never expand and must read as top-level.
  const { nestedIds, parentMap } = useMemo(() => {
    const map: Record<string, string[]> = {}
    const nested = new Set<string>()
    let currentParentId: string | null = null
    for (const h of headings) {
      if (h.level <= 2) {
        currentParentId = h.id
        map[currentParentId] = []
      } else if (currentParentId) {
        map[currentParentId].push(h.id)
        nested.add(h.id)
      }
    }
    return { nestedIds: nested, parentMap: map }
  }, [headings])

  // Determine which parent section is active (the h2 whose children include activeId, or the h2 itself)
  const activeParentId = useMemo(() => {
    if (!activeId) return null
    // If activeId is a parent itself
    if (parentMap[activeId]) return activeId
    // Find which parent owns this child
    for (const [parentId, childIds] of Object.entries(parentMap)) {
      if (childIds.includes(activeId)) return parentId
    }
    return null
  }, [activeId, parentMap])

  // Pre-compute which child headings should be visible
  const visibleChildIds = useMemo(() => {
    const ids = new Set<string>()
    if (activeParentId && parentMap[activeParentId]) {
      for (const childId of parentMap[activeParentId]) {
        ids.add(childId)
      }
    }
    return ids
  }, [activeParentId, parentMap])

  if (headings.length === 0) return null

  return (
    <nav aria-label="Table of contents" className="relative">
      <div className="border-l border-civo-cloud flex flex-col pl-4">
        {headings.map((heading) => {
          const isChild = nestedIds.has(heading.id)
          const isVisible = !isChild || visibleChildIds.has(heading.id)

          return (
            <div
              key={heading.id}
              className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                isVisible ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              }`}
            >
              <div className="overflow-hidden">
                <a
                  href={`#${heading.id}`}
                  onClick={(e) => {
                    e.preventDefault()
                    const el = document.getElementById(heading.id)
                    if (el) {
                      // An entry can point at a collapsed disclosure, which scrolls to an empty row.
                      const details = el.closest('details') as HTMLDetailsElement | null
                      if (details) details.open = true
                      pin(heading.id)
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  }}
                  className={`block py-[5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-lime focus-visible:ring-offset-2 ${
                    isChild ? 'body-small pl-4' : 'body-copy-medium'
                  } ${
                    activeId === heading.id ? 'text-black' : 'text-black/70 hover:text-black'
                  }`}
                  data-heading-id={heading.id}
                >
                  {heading.text}
                </a>
              </div>
            </div>
          )
        })}
      </div>

      {/* Active indicator - 2px black bar on the left */}
      <ActiveIndicator activeId={activeId} activeParentId={activeParentId} />
    </nav>
  )
}

function ActiveIndicator({
  activeId,
  activeParentId,
}: {
  activeId: string
  activeParentId: string | null
}) {
  const barRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const bar = barRef.current
    if (!bar) return

    const parent = bar.parentElement
    if (!parent || !activeId) {
      bar.style.opacity = '0'
      return
    }

    const updatePosition = () => {
      const link = parent.querySelector(`[data-heading-id="${activeId}"]`) as HTMLElement | null
      if (!link) {
        bar.style.opacity = '0'
        return
      }

      const parentRect = parent.getBoundingClientRect()
      const linkRect = link.getBoundingClientRect()

      bar.style.top = `${linkRect.top - parentRect.top}px`
      bar.style.height = `${linkRect.height}px`
      bar.style.opacity = '1'
    }

    // Initial position after layout
    requestAnimationFrame(updatePosition)

    // Reposition after expand/collapse animations finish
    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === 'grid-template-rows') {
        updatePosition()
      }
    }
    parent.addEventListener('transitionend', handleTransitionEnd)

    return () => {
      parent.removeEventListener('transitionend', handleTransitionEnd)
    }
  }, [activeId, activeParentId])

  return (
    <div
      ref={barRef}
      className="absolute left-0 w-0.5 bg-black transition-[top,height,opacity] duration-200 ease-out"
      style={{ opacity: 0 }}
    />
  )
}
